import { Ref, createContext, useContext } from "react"

import * as TabsPrimitive from "@radix-ui/react-tabs"
import { match } from "ts-pattern"

import { cn } from "@/lib/utils"

interface TabsContext {
  variant: "default" | "tag"
}

const TabsContext = createContext<TabsContext>({
  variant: "default",
})

export function Root({
  variant = "default",
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
  variant?: TabsContext["variant"]
  ref?: Ref<React.ComponentRef<typeof TabsPrimitive.Root>>
}) {
  return (
    <TabsContext.Provider value={{ variant }}>
      <TabsPrimitive.Root ref={ref} {...props} />
    </TabsContext.Provider>
  )
}

export function Content({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content> & {
  ref?: Ref<React.ComponentRef<typeof TabsPrimitive.Content>>
}) {
  return (
    <TabsPrimitive.Content ref={ref} className={cn("min-h-0", className)} {...props} />
  )
}

export function List({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & {
  ref?: Ref<React.ComponentRef<typeof TabsPrimitive.List>>
}) {
  const { variant } = useContext(TabsContext)

  const listClasses = match(variant)
    .with(
      "default",
      () => /*tw*/ "gap mx-2 mb-3 flex gap-10 border-b border-solid border-border",
    )
    .with("tag", () => /*tw*/ "gap flex gap-6")
    .exhaustive()

  return (
    <TabsPrimitive.List ref={ref} className={cn(listClasses, className)} {...props} />
  )
}

export function Trigger({
  children,
  className,
  disabled,
  ref,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & {
  disabled?: boolean
  ref?: Ref<React.ComponentRef<typeof TabsPrimitive.Trigger>>
}) {
  const { variant } = useContext(TabsContext)

  const triggerClasses = match(variant)
    .with(
      "default",
      () =>
        /*tw*/ `${
          disabled
            ? "cursor-default text-grey-400 border-transparent"
            : "flex cursor-pointer items-center gap-2 border-b border-solid border-transparent pb-2 text-grey-700 hover:border-border hover:text-grey-900 aria-selected:border-black aria-selected:text-grey-900"
        }`,
    )
    .with(
      "tag",
      () =>
        /*tw*/ `${
          disabled
            ? "cursor-default text-grey-400"
            : "flex cursor-pointer items-center gap-2 rounded-md p-2 px-3 text-grey-700 aria-selected:bg-accent aria-selected:text-accent-foreground transition-all hover:bg-accent"
        }`,
    )
    .exhaustive()

  return (
    <TabsPrimitive.Trigger
      className={cn("text-body1 whitespace-nowrap", triggerClasses, className)}
      {...props}
      ref={ref}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {children}
    </TabsPrimitive.Trigger>
  )
}
